import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Box,
  Typography,
  TextField,
  Button,
  List,
  ListItem,
  Avatar,
  Divider,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';

const ConversationModal = ({
  open,
  onClose,
  shopName,
  serviceRequestId,
  comments,
  loading,
  onSendMessage,
  currentUserType, // 'customer' or 'shop'
}) => {
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);

  // Auto-scroll to bottom when modal opens or comments change
  useEffect(() => {
    if (open && !loading && comments && comments.length > 0) {
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        } else if (messagesContainerRef.current) {
          messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
        }
      }, 100);
    }
  }, [open, loading, comments]);

  // Also scroll when a new message is sent
  useEffect(() => {
    if (!sending && comments && comments.length > 0) {
      setTimeout(() => {
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        } else if (messagesContainerRef.current) {
          messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
        }
      }, 100);
    }
  }, [sending, comments]);

  const handleSend = async () => {
    if (!newMessage.trim()) return;

    setSending(true);
    try {
      await onSendMessage(newMessage);
      setNewMessage('');
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '80vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: '#f8f9fa',
          borderBottom: '1px solid #e0e0e0',
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Conversation
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {shopName}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Messages List */}
            <Box 
              ref={messagesContainerRef}
              sx={{ flex: 1, overflowY: 'auto', p: 2, minHeight: 300, maxHeight: 400 }}
            >
              {!comments || comments.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No messages yet. Start the conversation!
                  </Typography>
                </Box>
              ) : (
                <List sx={{ p: 0 }}>
                  {comments.map((comment, index) => {
                    const alignRight =
                      comment.author_type === 'customer' ||
                      comment.author_type === 'household';

                    return (
                      <React.Fragment key={comment.id || index}>
                        <ListItem
                          sx={{
                            flexDirection: 'column',
                            alignItems: alignRight ? 'flex-end' : 'flex-start',
                            px: 0,
                            py: 1,
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: alignRight ? 'row-reverse' : 'row',
                              alignItems: 'flex-start',
                              width: '100%',
                              maxWidth: '85%',
                            }}
                          >
                            <Avatar
                              sx={{
                                width: 32,
                                height: 32,
                                mx: 1,
                                bgcolor: alignRight ? '#e63926' : '#4285F4',
                              }}
                            >
                              {comment.author_name?.[0] || '?'}
                            </Avatar>
                            <Box sx={{ flex: 1 }}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  justifyContent: alignRight ? 'flex-end' : 'flex-start',
                                  mb: 0.5,
                                }}
                              >
                                <Typography variant="caption" sx={{ fontWeight: 600, mr: 1 }}>
                                  {comment.author_name}
                                  {comment.author_type === 'household' && ' (Household)'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {console.log(comment.created_at)}
                                  {new Date(comment.created_at + 'Z').toLocaleString('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                    hour: 'numeric',
                                    minute: '2-digit',
                                  })}
                                  
                                </Typography>
                              </Box>
                              <Box
                                sx={{
                                  p: 1.5,
                                  borderRadius: 2,
                                  bgcolor: alignRight ? '#ffeef0' : '#f1f6ff',
                                  borderLeft: alignRight
                                    ? '3px solid #e63926'
                                    : '3px solid #4285F4',
                                }}
                              >
                                <Typography variant="body2">{comment.comment}</Typography>
                              </Box>
                            </Box>
                          </Box>
                        </ListItem>
                        {index < comments.length - 1 && <Divider sx={{ my: 1 }} />}
                      </React.Fragment>
                    );
                  })}
                  {/* Invisible element at the end to scroll to */}
                  <div ref={messagesEndRef} />
                </List>
              )}
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          p: 2,
          borderTop: '1px solid #e0e0e0',
          bgcolor: '#f8f9fa',
          flexDirection: 'column',
          alignItems: 'stretch',
        }}
      >
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            fullWidth
            multiline
            maxRows={3}
            placeholder="Type your message..."
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={sending}
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: 'white',
              },
            }}
          />
          <Button
            variant="contained"
            onClick={handleSend}
            disabled={!newMessage.trim() || sending}
            sx={{
              bgcolor: '#e63926',
              '&:hover': {
                bgcolor: '#c62f1e',
              },
              minWidth: 100,
            }}
            startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
          >
            {sending ? 'Sending...' : 'Send'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default ConversationModal;

